/**
 * Client preferences (03 §3.3, 06 §1.10), stored in `localStorage['tarock.prefs.v1']`. When signed in they sync to
 * `prefs_json` (`stores/auth.ts`); on sign-in the server's values win (`applyServerPrefs`).
 */
import { signal } from '@preact/signals';

/** Animation speed (06 §7.3). */
type Speed = 'slow' | 'normal' | 'fast';

/** The preferences in 03 §3.3 order (the P2 subset plus the later keys' defaults). */
export interface Prefs {
  speed: Speed;
  reducedMotion: 'system' | 'on' | 'off';
  playGesture: 'twoTap' | 'oneTap';
  autoDone: boolean;
  tarockOrder: 'desc' | 'asc';
  trickCountBadges: boolean;
  /** My own card-point display during play (03 §7.7): `table` follows `showCardPointTotals`. */
  cardPoints: 'table' | 'on' | 'off';
  sound: boolean;
  haptics: boolean;
  largeHand: boolean;
  renderer: 'auto' | '2d' | '3d';
  preferredDeck: string;
  modernFourColour: boolean;
  modernHighContrast: boolean;
  hideChat: boolean;
  mutedUsers: string[];
  /** 03 §11.5: on the leaderboards, and stats on my player card. */
  showOnLeaderboard: boolean;
  /** tile.game (16 §11.3): dim unplayable tiles and show targets on selection. */
  placementHints: boolean;
  /**
   * tile.game (16 §11.1, 17 §4.4; ADR 0066): the tile theme of my tables, or `auto`, each set's own (`ivory`, the
   * colour-coded `colour` for Mexican Train's double-12). Replaces `tileTheme`, whose stored `ivory` was only the
   * default.
   */
  tileStyle: 'auto' | 'ivory' | 'ebony' | 'colour' | 'highContrast';
  /** tile.game (16 §6.3): the rack order. */
  tileSort: 'value' | 'number' | 'manual';
  /** tile.game (16 §7.4): ask before a voluntary draw (Triominoes). */
  confirmVoluntaryDraw: boolean;
  /** tile.game (16 §8.5, ADR 0072): the other players' tiles as rows of backs at tables of up to 4 seats. */
  tileHandBacks: boolean;
}

const KEY = 'tarock.prefs.v1';

const media = (q: string): boolean => typeof matchMedia === 'function' && matchMedia(q).matches;

/** The defaults, some of which depend on the device (03 §3.3). */
function defaultPrefs(): Prefs {
  return {
    speed: 'normal',
    reducedMotion: 'system',
    playGesture: media('(pointer: coarse)') ? 'twoTap' : 'oneTap',
    autoDone: true,
    tarockOrder: 'desc',
    trickCountBadges: false,
    cardPoints: 'table',
    sound: false,
    haptics: true,
    largeHand: false,
    renderer: 'auto',
    // 03 §3.3 (P6): the historic Vienna deck; a deck the index does not list yet falls back to `modern` (pickDeck)
    preferredDeck: 'iug-1903',
    modernFourColour: false,
    modernHighContrast: media('(prefers-contrast: more)'),
    hideChat: false,
    mutedUsers: [],
    showOnLeaderboard: true,
    placementHints: true,
    tileStyle: 'auto',
    tileSort: 'value',
    confirmVoluntaryDraw: true,
    tileHandBacks: true,
  };
}

/** The themes a retired `tileTheme` may carry over into `tileStyle` (its `ivory` was the default, so it means `auto`). */
const CHOSEN_THEMES: ReadonlySet<unknown> = new Set(['ebony', 'colour', 'highContrast']);

/** `base` with every key of `stored` whose value has the default's type. */
function merged(base: Prefs, stored: Partial<Record<keyof Prefs | 'tileTheme', unknown>>): Prefs {
  const out: Record<string, unknown> = { ...base };
  for (const k of Object.keys(base) as (keyof Prefs)[]) {
    const v = stored[k];
    if (v !== undefined && typeof v === typeof base[k] && Array.isArray(v) === Array.isArray(base[k])) {
      out[k] = v;
    }
  }
  // a theme chosen before `tileStyle` (ADR 0066) stays chosen
  if (stored.tileStyle === undefined && CHOSEN_THEMES.has(stored.tileTheme)) {
    out['tileStyle'] = stored.tileTheme;
  }
  return out as unknown as Prefs;
}

function load(): Prefs {
  const base = defaultPrefs();
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? merged(base, JSON.parse(raw) as Partial<Record<keyof Prefs | 'tileTheme', unknown>>) : base;
  } catch {
    return base;
  }
}

function save(): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefsStore.value));
  } catch {
    // storage full or disabled: the preference still applies to this tab
  }
}

/** The live preferences. */
export const prefsStore = signal<Prefs>(load());

/** Changes one preference and persists the set. */
export function setPref<K extends keyof Prefs>(key: K, value: Prefs[K]): void {
  prefsStore.value = { ...prefsStore.value, [key]: value };
  save();
}

/**
 * The preferences that sync to the account: all but the device-only `renderer` (03 §3.3), since a phone and a laptop
 * want different table views.
 */
export function syncedPrefs(p: Prefs = prefsStore.value): Omit<Prefs, 'renderer'> {
  const { renderer: _deviceOnly, ...synced } = p;
  return synced;
}

/** On sign-in the server's values win (03 §3.3); keys the server does not have, and `renderer`, keep the local value. */
export function applyServerPrefs(server: object): void {
  const { renderer: _deviceOnly, ...remote } = server as Partial<Record<keyof Prefs | 'tileTheme', unknown>>;
  prefsStore.value = merged(prefsStore.value, remote);
  save();
}

/** The table options that decide what the viewer may see of card points. */
interface PointOptions {
  showCardPointTotals: boolean;
  viewOwnTricks: boolean;
}

/**
 * Whether card points show during play for this viewer (03 §7.7): the table's `showCardPointTotals` unless my
 * `cardPoints` preference says otherwise. "On" needs a table that lets me look through my own tricks (01 §7.5), so
 * the preference never shows what the table's rules withhold.
 */
export function showCardPoints(o: PointOptions | undefined, p: Prefs = prefsStore.value): boolean {
  if (!o || p.cardPoints === 'off') {
    return false;
  }
  return o.showCardPointTotals || (p.cardPoints === 'on' && o.viewOwnTricks);
}

/** Whether reduced motion applies (06 §7.5). */
export function reducedMotion(p: Prefs = prefsStore.value): boolean {
  return p.reducedMotion === 'on' || (p.reducedMotion === 'system' && media('(prefers-reduced-motion: reduce)'));
}
